import React from "react";
import {Box, IconButton, Modal, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {useTranslation} from "react-i18next";

/**
 * Fullscreen overlay that shows a single image at its natural aspect ratio.
 *
 * Props:
 *   - open       boolean
 *   - src        string  — URL to display (server URL or `URL.createObjectURL`)
 *   - title      string  — caption shown at the bottom (file name / guid)
 *   - onClose    () => void
 *   - onDelete   () => void  — when undefined, the trash icon is hidden
 *                              (e.g. read-only view)
 */
const ImagePreviewModal = ({open, src, title, onClose, onDelete}) => {
    const {t} = useTranslation();
    const handleDelete = () => {
        if (!onDelete) return;
        const ok = window.confirm(t("forms.transaction.images.confirmDelete"));
        if (ok) onDelete();
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="image-preview">
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0, 0, 0, 0.92)",
                    display: "flex",
                    flexDirection: "column",
                    outline: "none",
                }}
                onClick={onClose}
            >
                <Box sx={{display: "flex", justifyContent: "space-between", p: 1, color: "white"}}>
                    <IconButton onClick={onClose} sx={{color: "white"}} aria-label={t("common.buttons.close")}>
                        <CloseIcon />
                    </IconButton>
                    {onDelete && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete();
                            }}
                            sx={{color: "white"}}
                            aria-label={t("common.buttons.delete")}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    )}
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        px: 2,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {src ? (
                        <img
                            src={src}
                            alt={title || ""}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                            }}
                        />
                    ) : (
                        <Typography color="white">{t("forms.transaction.images.notLoaded")}</Typography>
                    )}
                </Box>
                {title && (
                    <Box sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="body2" sx={{color: "rgba(255,255,255,0.85)"}}>
                            {title}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default ImagePreviewModal;
